declare global {
  interface Window {
    OnlineHelpAPI?: {
      site_id?: string;
      main_code_plan?: string;
      chat_buttons?: Array<{
        code_plan: string;
        div_id: string;
      }>;
      open_chat_window?: () => void;
      end_chat?: () => void;
      loaded?: boolean;
      [key: string]: any;
    };
  }
}

export type Chat100Config = {
  siteId: string;
  codePlan: string;
};

export function Chat100Manager() {
  const SCRIPT_ID = "onlinehelp-script";
  const CONTAINER_PREFIX = "onlinehelp-button-";

  let activeSiteId: string | null = null;
  let activeCodePlan: string | null = null;

  let isReady = false;
  let isChatWindowOpen = false;

  let loadingPromise: Promise<void> | null = null;
  let isCleaningUp = false;

  const wait = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

  const getApi = () => window.OnlineHelpAPI;

  const isSameConfig = (config: Chat100Config) => {
    return activeSiteId === config.siteId && activeCodePlan === config.codePlan;
  };

  const waitForReady = async (
    config: Chat100Config,
    maxAttempts = 40,
    interval = 250,
  ) => {
    const divId = `${CONTAINER_PREFIX}${config.codePlan}`;

    for (let i = 0; i < maxAttempts; i++) {
      const api = getApi();

      const container = document.getElementById(divId);

      const hasWidget =
        !!container &&
        (container.children.length > 0 ||
          !!document.querySelector('iframe[src*="livechat"]') ||
          !!document.querySelector("#chat_window_container") ||
          !!document.querySelector('iframe[title="Chat button"]') ||
          !!document.querySelector('iframe[title="Chat icon for chat window"]') ||
          !!document.querySelector('iframe[title="Chat Window"]') ||
          !!document.querySelector("#chat_button_adaptive_container"));

      const hasOpenApi = typeof api?.open_chat_window === "function";

      if (api?.loaded || hasOpenApi || hasWidget) {
        return;
      }

      await wait(interval);
    }

    throw new Error("Chat100 widget failed to initialize");
  };

  const resetManagerState = () => {
    isReady = false;
    isChatWindowOpen = false;

    activeSiteId = null;
    activeCodePlan = null;

    loadingPromise = null;
  };

  const endCurrentChat = async () => {
    try {
      if (typeof window.OnlineHelpAPI?.end_chat === "function") {
        window.OnlineHelpAPI.end_chat();
      }
    } catch (error) {
      console.warn("Chat100 end_chat failed:", error);
    }

    await wait(80);
  };

  const removeScript = () => {
    document.getElementById(SCRIPT_ID)?.remove();

    document
      .querySelectorAll('script[src*="livechat.ashx"]')
      .forEach((el) => el.remove());
  };

  const removeContainers = () => {
    document
      .querySelectorAll(`[id^="${CONTAINER_PREFIX}"]`)
      .forEach((el) => el.remove());
  };

  const removeInjectedDom = () => {
    document
      .querySelectorAll(
        [
          'iframe[src*="livechat"]',
          "#chat_button_adaptive_container",
          "#chat_window_container",
          '[class*="onlinehelp"]',
          '[id*="onlinehelp"]',
        ].join(","),
      )
      .forEach((el) => {
        const htmlEl = el as HTMLElement;

        if (htmlEl.id !== "root" && !htmlEl.id.startsWith(CONTAINER_PREFIX)) {
          htmlEl.remove();
        }
      });
  };

  const resetWindowObjects = () => {
    try {
      delete window.OnlineHelpAPI;
    } catch {
      window.OnlineHelpAPI = undefined;
    }
  };

  const cleanupAndDestroyWidget = async () => {
    if (isCleaningUp) return;

    isCleaningUp = true;

    try {
      await endCurrentChat();

      removeScript();
      removeContainers();
      removeInjectedDom();
      resetWindowObjects();

      await wait(80);

      resetManagerState();
    } finally {
      isCleaningUp = false;
    }
  };

  const injectScript = (src: string) => {
    return new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(SCRIPT_ID);

      if (existing) {
        existing.remove();
      }

      const script = document.createElement("script");

      script.id = SCRIPT_ID;
      script.src = src;
      script.async = true;
      script.type = "text/javascript";

      script.onload = () => resolve();

      script.onerror = () => {
        script.remove();

        reject(new Error(`Failed loading script: ${src}`));
      };

      document.body.appendChild(script);
    });
  };

  const loadProviderScript = async (siteId: string) => {
    try {
      await injectScript(
        `https://b7rnj.77udet04.com/livechat.ashx?siteId=${siteId}`,
      );
    } catch {
      await injectScript(
        `https://ey4ga7r9.cdqvo5pl.com/livechat.ashx?siteId=${siteId}`,
      );
    }
  };

  const createFreshWidget = async (config: Chat100Config) => {
    const divId = `${CONTAINER_PREFIX}${config.codePlan}`;

    const oldDiv = document.getElementById(divId);

    if (oldDiv) {
      oldDiv.remove();
    }

    const div = document.createElement("div");

    div.id = divId;

    document.body.appendChild(div);

    window.OnlineHelpAPI = {
      site_id: config.siteId,
      main_code_plan: config.codePlan,
      chat_buttons: [
        {
          code_plan: config.codePlan,
          div_id: divId,
        },
      ],
    };

    await loadProviderScript(config.siteId);

    await waitForReady(config);

    activeSiteId = config.siteId;
    activeCodePlan = config.codePlan;

    isReady = true;
    isChatWindowOpen = false;
  };

  const load = async (config: Chat100Config) => {
    if (isSameConfig(config) && isReady) {
      return;
    }

    if (loadingPromise) {
      await loadingPromise;

      if (isSameConfig(config) && isReady) {
        return;
      }
    }

    loadingPromise = createFreshWidget(config);

    try {
      await loadingPromise;
    } finally {
      loadingPromise = null;
    }
  };

  const clickOrOpenChatWindow = async (config: Chat100Config) => {
    await wait(50);

    getApi()?.open_chat_window?.();

    isChatWindowOpen = true;

    const launcher = document.getElementById(
      `${CONTAINER_PREFIX}${config.codePlan}`,
    );

    if (launcher) {
      launcher.style.opacity = "0";
      launcher.style.pointerEvents = "none";
    }

    const iframe = document.querySelector(
      "#chat_window_container",
    ) as HTMLElement | null;

    if (iframe) {
      iframe.style.display = "block";
    }
  };

  const open = async (config: Chat100Config) => {
    if (loadingPromise) {
      await loadingPromise;
    }

    const sameConfig = isSameConfig(config) && isReady;

    if (sameConfig && isChatWindowOpen) {
      const iframe = document.querySelector(
        "#chat_window_container",
      ) as HTMLElement | null;

      if (iframe) {
        iframe.style.display = "none";
      }

      isChatWindowOpen = false;

      return;
    }

    if (!sameConfig) {
      await cleanupAndDestroyWidget();

      loadingPromise = createFreshWidget(config);

      try {
        await loadingPromise;
      } finally {
        loadingPromise = null;
      }

      await clickOrOpenChatWindow(config);

      return;
    }

    if (!isChatWindowOpen) {
      await clickOrOpenChatWindow(config);
    }
  };

  const close = async () => {
    const iframe = document.querySelector(
      "#chat_window_container",
    ) as HTMLElement | null;

    if (iframe) {
      iframe.style.display = "none";
    }

    isChatWindowOpen = false;
  };

  const destroy = async () => {
    await cleanupAndDestroyWidget();
  };

  return {
    load,
    open,
    close,
    destroy,
  };
}

const chat100 = Chat100Manager();

export default chat100;
